import { ref, Ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

export function useNavigationPrompt(isDirty: Ref<boolean>) {
    const showPrompt = ref(false);

    let dialogResolve: ((ok: boolean) => void) | undefined = undefined;
    const onDialogClosed = (ok: boolean) => {
        if (dialogResolve) {
            dialogResolve(ok);
            dialogResolve = undefined;
        }
    };

    const checkNavigation = async () => {
        if (isDirty.value) {
            showPrompt.value = true;

            return await new Promise<boolean>((r) => {
                dialogResolve = r;
            });
        }

        return true;
    };

    const onBackClicked = async () => {
        if (await checkNavigation()) {
            window.history.go(-1);
        }
    };

    /**
     * Intercept user navigation using the Navigation API (https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API)
     * @remarks This API doesn't let us prevent cross-domain navigation or the UI's back/forward buttons.
     */
    function addNavigationEventListener() {
        // Types for the Navigation API aren't available in the current version of TS
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const n = window.navigation;
        if (!n) {
            // This won't work without the Navigation API, leave early.
            return;
        }

        const continueNavigation = (
            type: 'push' | 'reload' | 'replace' | 'traverse',
            key: string,
            url: string,
            opts: { info: unknown; state: unknown }
        ) => {
            if (type === 'traverse') {
                n.traverseTo(key, { info: opts.info });
            } else if (type === 'reload') {
                n.reload(opts);
            } else if (type === 'replace') {
                n.updateCurrentEntry({ state: opts.state });
            } else {
                n.navigate(url, opts);
            }
        };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        n.addEventListener('navigate', (event) => {
            if (
                !event.userInitiated ||
                !event.cancelable ||
                event.formData ||
                event.hashChange ||
                event.downloadRequest
            ) {
                // ignore navigation types we can't manipulate
                return;
            }

            if (!isDirty.value) {
                return;
            }
            event.preventDefault();
            checkNavigation().then((ok) => {
                if (ok) {
                    continueNavigation(
                        event.navigationType,
                        event.destination.key,
                        event.destination.url,
                        {
                            info: event.info,
                            state: event.destination.getState()
                        });
                }
            });
        });
    }

    addNavigationEventListener();
    onBeforeRouteLeave(() => {
        return checkNavigation();
    });

    return {
        showPrompt,
        checkNavigation,
        onDialogClosed,
        onBackClicked
    };
}